import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiClientError } from '../../services/api';

type QrState = 'idle' | 'loading' | 'waiting' | 'paired' | 'error';

const DevicePairing = () => {
  const [state, setState] = useState<QrState>('idle');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(90);
  const tokenRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    pollRef.current = null;
    countdownRef.current = null;
  }, []);

  const startPairing = useCallback(async () => {
    cleanup();
    setState('loading');
    setError(null);
    setQrDataUrl(null);
    try {
      const result = await api.startQrSession();
      tokenRef.current = result.token;
      setQrDataUrl(result.qrDataUrl);
      setTimeLeft(90);
      setState('waiting');

      pollRef.current = setInterval(async () => {
        if (!tokenRef.current) return;
        try {
          const status = await api.getQrSessionStatus(tokenRef.current);
          if (status.status === 'consumed') {
            cleanup();
            setState('paired');
            setTimeout(() => setState('idle'), 3_000);
          } else if (status.status === 'expired') {
            cleanup();
            setState('error');
            setError('De QR-code is verlopen. Probeer het opnieuw.');
          }
        } catch {
          // ignore polling errors
        }
      }, 2_000);

      countdownRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            cleanup();
            setState('error');
            setError('De QR-code is verlopen. Probeer het opnieuw.');
            return 0;
          }
          return prev - 1;
        });
      }, 1_000);
    } catch (reason) {
      setState('error');
      setError(
        reason instanceof ApiClientError
          ? reason.message
          : 'Kon QR-sessie niet starten.',
      );
    }
  }, [cleanup]);

  useEffect(() => cleanup, [cleanup]);

  if (state === 'idle') {
    return (
      <div className="device-pairing">
        <h3>Apparaat koppelen</h3>
        <p className="muted">
          Koppel je telefoon of ander apparaat door een QR-code te scannen.
          De koppeling blijft actief totdat je deze handmatig verwijdert.
        </p>
        <button
          type="button"
          className="button primary small"
          onClick={() => void startPairing()}
        >
          Nieuw apparaat koppelen
        </button>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className="device-pairing">
        <h3>Apparaat koppelen</h3>
        <p className="muted">QR-code genereren…</p>
      </div>
    );
  }

  if (state === 'paired') {
    return (
      <div className="device-pairing">
        <h3>Apparaat gekoppeld</h3>
        <p className="notice success">
          Het apparaat is succesvol gekoppeld aan je account.
        </p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="device-pairing">
        <h3>Apparaat koppelen</h3>
        {error && <p className="notice error">{error}</p>}
        <button
          type="button"
          className="button primary small"
          onClick={() => void startPairing()}
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }

  return (
    <div className="device-pairing">
      <h3>Apparaat koppelen</h3>
      <p className="muted">
        Scan deze QR-code met je telefoon. Geldig nog {timeLeft}s.
      </p>
      {qrDataUrl && (
        <img
          src={qrDataUrl}
          alt="QR-code voor apparaatkoppeling"
          width={280}
          height={280}
        />
      )}
      <p className="muted small">
        Wachten op scan…
      </p>
      <button
        type="button"
        className="button secondary small"
        onClick={() => {
          cleanup();
          setState('idle');
        }}
      >
        Annuleren
      </button>
    </div>
  );
};

export default DevicePairing;
